import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, Send, Eye, ShieldCheck, CheckCircle2, RotateCcw, Archive, Upload, X, Loader2, ArrowLeft, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, CONTENT_TYPE_LABELS } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, STATUS_LABELS, CONTENT_TYPES, PROGRAM_LABELS } from "@/lib/adminConst";
import RichTextEditor from "@/components/admin/RichTextEditor";
import DatePicker from "@/components/admin/DatePicker";
import { Button } from "@/components/ui/button";

const EMPTY = {
  title: "", slug: "", summary: "", content_type: "berita_kegiatan", responsible_unit: "",
  collaborating_units: [], subunits: [], topics: [], tags: [], program_labels: [], body_html: "",
  featured_media_url: "", featured_media_id: "", featured_caption: "", featured_credit: "", featured_alt: "",
  gallery: [], video_links: [], event_location: "", event_date: "", author_name: "", sources: [],
  public_attachments: [], meta_title: "", meta_description: "", internal_notes: "",
};

export default function ContentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [units, setUnits] = useState([]);
  const [topics, setTopics] = useState([]);
  const [status, setStatus] = useState("draft");
  const [createdBy, setCreatedBy] = useState(null);
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    api.get("/public/units").then((r) => setUnits(r.data));
    api.get("/public/topics").then((r) => setTopics(r.data));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/articles/${id}`).then((r) => {
      const a = r.data;
      setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, a[k] ?? EMPTY[k]])) });
      setStatus(a.status); setCreatedBy(a.created_by); setCorrection(a.correction_note || "");
      setLoaded(true);
    }).catch(() => { toast.error("Gagal memuat artikel"); navigate("/admin/konten"); });
  }, [id, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const selectedUnit = units.find((u) => u.slug === form.responsible_unit);

  const save = useCallback(async (silent = false) => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return null; }
    if (!form.responsible_unit) { toast.error("Unit penanggung jawab wajib dipilih"); return null; }
    if (form.topics.length === 0) { toast.error("Pilih minimal satu topik"); return null; }
    setSaving(true);
    try {
      let res;
      if (id) res = await api.put(`/articles/${id}`, form);
      else res = await api.post("/articles", form);
      if (!silent) toast.success("Tersimpan");
      if (!id) { navigate(`/admin/konten/${res.data.id}`, { replace: true }); }
      else { setStatus(res.data.status); }
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
      return null;
    } finally { setSaving(false); }
  }, [form, id, navigate]);

  const runAction = async (artId, action, note = "") => {
    const r = await api.post(`/articles/${artId}/${action}`, { note });
    return r.data.status;
  };

  const doAction = async (action, note = "") => {
    // Only persist edits for creation or submit. For verify/approve/publish/return,
    // saving a verified/approved article would reset its status, so we skip saving.
    let artId = id;
    if (!id || action === "submit") {
      const saved = await save(true);
      artId = saved?.id || id;
      if (!artId) return;
    }
    try {
      const st = await runAction(artId, action, note);
      setStatus(st);
      toast.success(`Status: ${STATUS_LABELS[st] || st}`);
      if (action === "publish") navigate("/admin/konten");
    } catch (e) { toast.error(e.response?.data?.detail || "Aksi gagal"); }
  };

  const approveAndPublish = async () => {
    if (!id) return;
    try {
      await runAction(id, "approve");
      const st = await runAction(id, "publish");
      setStatus(st);
      toast.success("Berita disetujui & diterbitkan");
      navigate("/admin/konten");
    } catch (e) { toast.error(e.response?.data?.detail || "Aksi gagal"); }
  };

  const uploadFeatured = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("purpose", "image");
    try {
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("featured_media_url", r.data.url); set("featured_media_id", r.data.id);
      toast.success("Foto diunggah");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah"); }
    finally { setUploading(false); }
  };

  if (!loaded) return <p className="text-gray-500">Memuat…</p>;

  const isSuper = user?.role === "super_admin";
  const isEditorOfUnit = user?.role === "editor" && (user?.units || []).includes(form.responsible_unit);
  const isPublisher = user?.role === "publisher" || isSuper;
  const notOwn = createdBy !== user?.user_id;
  const canVerify = (isSuper || isEditorOfUnit) && status === "pending_review" && (notOwn || isSuper);
  const canApprove = isPublisher && status === "verified" && (notOwn || isSuper);
  const canPublish = isPublisher && status === "approved";
  const canReturn = (isSuper || isEditorOfUnit || isPublisher) && ["pending_review", "verified"].includes(status);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/konten" className="p-2 rounded hover:bg-gray-100"><ArrowLeft className="h-5 w-5 text-gray-500" /></Link>
          <h1 className="font-serif text-2xl font-extrabold text-dlh-green">{id ? "Edit Berita" : "Tulis Berita"}</h1>
          <StatusBadge status={status} />
        </div>
      </div>

      {status === "needs_revision" && correction && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800"><strong>Catatan revisi:</strong> {correction}</div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Field label="Judul *">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="field-title"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-lg font-serif focus:outline-none focus:ring-2 focus:ring-dlh-orange" />
          </Field>
          <Field label="Slug (URL)"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="otomatis dari judul" data-testid="field-slug" className="input" /></Field>
          <Field label="Ringkasan"><textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} data-testid="field-summary" className="input" /></Field>

          <Field label="Isi Artikel">
            <RichTextEditor value={form.body_html} onChange={(v) => set("body_html", v)} />
          </Field>

          <Field label="Foto Utama">
            {form.featured_media_url ? (
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                <img src={mediaUrl(form.featured_media_url)} alt="" className="w-full max-h-64 object-cover" />
                <button onClick={() => { set("featured_media_url", ""); set("featured_media_id", ""); }} className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-dlh-green" data-testid="upload-featured">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin text-dlh-green" /> : <><Upload className="h-6 w-6 text-gray-400 mb-2" /><span className="text-sm text-gray-500">Unggah foto (JPG/PNG/WebP, maks 8MB)</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFeatured(e.target.files[0])} />
              </label>
            )}
            {form.featured_media_url && <div className="grid grid-cols-3 gap-2 mt-2">
              <input value={form.featured_caption} onChange={(e) => set("featured_caption", e.target.value)} placeholder="Caption" className="input text-sm" />
              <input value={form.featured_credit} onChange={(e) => set("featured_credit", e.target.value)} placeholder="Kredit foto" className="input text-sm" />
              <input value={form.featured_alt} onChange={(e) => set("featured_alt", e.target.value)} placeholder="Alt text" className="input text-sm" />
            </div>}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Lokasi Kegiatan"><input value={form.event_location} onChange={(e) => set("event_location", e.target.value)} className="input" /></Field>
            <Field label="Tanggal Kegiatan"><DatePicker value={form.event_date} onChange={(v) => set("event_date", v)} /></Field>
          </div>
          <Field label="Penulis / Redaksi"><input value={form.author_name} onChange={(e) => set("author_name", e.target.value)} className="input" /></Field>
          <Field label="Sumber Informasi (satu per baris, hanya yang boleh dipublikasikan)">
            <textarea value={(form.sources || []).join("\n")} onChange={(e) => set("sources", e.target.value.split("\n").filter(Boolean))} rows={2} className="input" />
          </Field>
          <Field label="Video (tautan YouTube/Vimeo, satu per baris)">
            <textarea value={(form.video_links || []).map((v) => v.url).join("\n")} onChange={(e) => set("video_links", e.target.value.split("\n").filter(Boolean).map((u) => ({ url: u.trim(), title: "" })))} rows={2} className="input" placeholder="https://youtube.com/watch?v=..." />
          </Field>

          <details className="bg-slate-50 rounded-lg p-4">
            <summary className="cursor-pointer font-semibold text-dlh-green text-sm">SEO & Catatan Internal</summary>
            <div className="mt-3 space-y-3">
              <input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} placeholder="Meta title" className="input text-sm" />
              <textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} placeholder="Meta description" rows={2} className="input text-sm" />
              <textarea value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} placeholder="Catatan internal redaksi (tidak terbit)" rows={2} className="input text-sm" data-testid="field-internal-notes" />
            </div>
          </details>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => save()} disabled={saving} data-testid="save-draft-btn" className="bg-dlh-green hover:bg-dlh-greenDark flex-1"><Save className="h-4 w-4 mr-1" /> Simpan</Button>
              {id && <Link to={`/admin/konten/${id}/pratinjau`} className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"><Eye className="h-4 w-4 mr-1" /> Pratinjau</Link>}
            </div>
            {["draft", "needs_revision"].includes(status) && (
              <Button onClick={() => doAction("submit")} data-testid="submit-review-btn" className="w-full bg-blue-600 hover:bg-blue-700"><Send className="h-4 w-4 mr-1" /> Ajukan Pemeriksaan</Button>
            )}
            {canVerify && <Button onClick={() => doAction("verify")} data-testid="verify-btn" className="w-full bg-indigo-600 hover:bg-indigo-700"><ShieldCheck className="h-4 w-4 mr-1" /> Verifikasi</Button>}
            {canApprove && <Button onClick={() => doAction("approve")} data-testid="approve-btn" className="w-full bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="h-4 w-4 mr-1" /> Setujui</Button>}
            {canApprove && <Button onClick={approveAndPublish} data-testid="approve-publish-btn" className="w-full bg-dlh-orange hover:bg-dlh-orangeDark"><Send className="h-4 w-4 mr-1" /> Setujui & Terbitkan</Button>}
            {canPublish && <Button onClick={() => doAction("publish")} data-testid="publish-btn" className="w-full bg-dlh-orange hover:bg-dlh-orangeDark"><Send className="h-4 w-4 mr-1" /> Terbitkan</Button>}
            {canReturn && <Button onClick={() => { const n = window.prompt("Catatan revisi (wajib):"); if (n) doAction("return", n); }} variant="outline" data-testid="return-btn" className="w-full border-amber-400 text-amber-700"><RotateCcw className="h-4 w-4 mr-1" /> Kembalikan untuk Revisi</Button>}
            {status === "published" && isPublisher && <Button onClick={() => { const n = window.prompt("Alasan penarikan:"); if (n) doAction("withdraw", n); }} variant="outline" className="w-full border-red-300 text-red-600"><Archive className="h-4 w-4 mr-1" /> Tarik / Arsipkan</Button>}
            {status === "published" && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-bold text-dlh-green mb-2">Bagikan Berita</p>
                <div className="flex gap-2">
                  <a href={`https://wa.me/?text=${encodeURIComponent(form.title + " " + window.location.origin + "/api/og/" + form.slug + "?u=" + encodeURIComponent(window.location.origin))}`} target="_blank" rel="noreferrer" data-testid="form-share-wa"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-white text-sm font-semibold px-3 py-2 rounded-lg bg-[#25D366] hover:opacity-90"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/berita/${form.slug}`); toast.success("Tautan disalin"); }} data-testid="form-share-copy"
                    className="inline-flex items-center justify-center gap-1.5 text-dlh-green text-sm font-semibold px-3 py-2 rounded-lg border border-dlh-green hover:bg-dlh-greenPale"><Link2 className="h-4 w-4" /> Salin</button>
                </div>
              </div>
            )}
            {createdBy === user?.user_id && !isSuper && ["verified", "approved"].includes(status) && (
              <p className="text-xs text-amber-600">Anda tidak dapat memverifikasi/menyetujui artikel Anda sendiri.</p>
            )}
          </div>

          <Panel title="Jenis Konten *">
            <select value={form.content_type} onChange={(e) => set("content_type", e.target.value)} data-testid="field-content-type" className="input">
              {CONTENT_TYPES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Panel>

          <Panel title="Unit Penanggung Jawab *">
            <select value={form.responsible_unit} onChange={(e) => { set("responsible_unit", e.target.value); set("subunits", []); }} data-testid="field-unit" className="input">
              <option value="">— Pilih Unit —</option>
              {units.map((u) => <option key={u.slug} value={u.slug}>{u.short_name}</option>)}
            </select>
          </Panel>

          {selectedUnit?.subunits?.length > 0 && (
            <Panel title={selectedUnit.is_support ? "Sub Bagian Terkait" : "Seksi Terkait"}>
              <ChipList items={selectedUnit.subunits.map((s) => s.name)} selected={form.subunits} onToggle={(v) => toggle("subunits", v)} />
            </Panel>
          )}

          <Panel title="Unit Kolaborasi">
            <ChipList items={units.filter((u) => u.slug !== form.responsible_unit).map((u) => u.slug)} labels={Object.fromEntries(units.map((u) => [u.slug, u.short_name]))} selected={form.collaborating_units} onToggle={(v) => toggle("collaborating_units", v)} />
          </Panel>

          <Panel title="Topik * (editorial)">
            <ChipList items={topics.map((t) => t.slug)} labels={Object.fromEntries(topics.map((t) => [t.slug, t.name]))} selected={form.topics} onToggle={(v) => toggle("topics", v)} testid="topics" />
          </Panel>

          <Panel title="Label Program">
            <ChipList items={PROGRAM_LABELS} selected={form.program_labels} onToggle={(v) => toggle("program_labels", v)} />
          </Panel>

          <Panel title="Tag (pisahkan koma)">
            <input value={(form.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className="input" />
          </Panel>
        </div>
      </div>

      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.95rem}.input:focus{outline:none;box-shadow:0 0 0 2px #F97316}`}</style>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>{children}</div>; }
function Panel({ title, children }) { return <div className="bg-white rounded-xl border border-gray-200 p-4"><h3 className="text-sm font-bold text-dlh-green mb-2">{title}</h3>{children}</div>; }

function ChipList({ items, labels = {}, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid ? `chips-${testid}` : undefined}>
      {items.map((it) => (
        <button key={it} type="button" onClick={() => onToggle(it)} data-testid={testid ? `chip-${testid}-${it}` : undefined}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors leading-snug text-left ${selected.includes(it) ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-600 border-gray-300 hover:border-dlh-green"}`}>
          {labels[it] || it}
        </button>
      ))}
    </div>
  );
}
